import React, { useEffect, useRef, useState } from "react";
import { InventoryApiService } from "../../Service/InventoryApiService";
import { DataGrid } from "@mui/x-data-grid";
import { Tooltip, IconButton, Box, TextField } from "@mui/material";
import "../Dynamic/UserList.scss";
import { CommonService } from "../../Service/CommonService";
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import HistoryIcon from '@mui/icons-material/History';
import { useHistory, useParams } from "react-router";
import BarChartIcon from '@mui/icons-material/BarChart';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Modal, Form } from "react-bootstrap";
import Spinner from "../Common/Spinner";
import DataGridLoader from "../Common/DataGridLoader";

// Custom cell renderer for the editable field
const EditableCell = ({ value, row, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [previousValue, setPreviousValue]  = useState(value || '');
  

  const handleSave = () => {
    // Allow empty OR numbers with up to 2 decimal places
    if (/^\d*\.?\d{0,2}$/.test(inputValue) || inputValue === '') {

      // Only call onSave if value actually changed
      if (inputValue !== previousValue) {
        onSave(row?.barrelLotValueId, inputValue, previousValue);
      }

      setIsEditing(false);
    } else {
      CommonService.Toast.fire({
        title: "Please enter a valid number (up to 2 decimal places).",
        icon: "warning",
      });
    }
  };


  const handleCancel = () => {
    setInputValue(value || '');
    setIsEditing(false);
  };

  const handleEditButtonClick = () => {
    const today = new Date();
  
    // Get the current quarter's starting month
    const currentMonth = today.getMonth(); // 0-11
    const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
  
    // 15th day from the start of the quarter
    const editDeadline = new Date(today.getFullYear(), quarterStartMonth, 15);

    // Compare dates only so the whole 15th day is allowed
    const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const deadlineDateOnly = new Date(editDeadline.getFullYear(), editDeadline.getMonth(), editDeadline.getDate());

    // Allow editing only within the first 15 days of the quarter
    if (todayDateOnly <= deadlineDateOnly) {
      setIsEditing(true);
    } else {
      CommonService.Toast.fire({
          title:"You can modify values only within the first 15 days of the current quarter.",
          icon: "error"
      });
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center',justifyContent:"unset" }}>
        <TextField
          value={inputValue}
          onChange={(e) => {
            // Only allow digits
            const rawValue = e.target.value.replace(/[^0-9.]/g, "");
            // Allow only numbers with up to 2 decimal places and max value 999999.99
            if (/^\d*\.?\d{0,2}$/.test(rawValue) && Number(rawValue) <= 999999.99) {
              setInputValue(rawValue);
            }
          }}

          onKeyDown={handleKeyPress}
          size="small"
          autoFocus
          sx={{ width:"100px",boxShadow:"unset",outline:"unset" }}
          placeholder="Market Value Per Value"
          className="insurance-value-edit-text-field"
        />
        <Tooltip title="Save" arrow>
        <IconButton size="small" onClick={handleSave} color="primary">
          <SaveIcon fontSize="small" />
        </IconButton>
        </Tooltip>

        <Tooltip title="Cancel" arrow>
        <IconButton size="small" onClick={handleCancel} color="secondary">
          <CancelIcon fontSize="small" />
        </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'unset',gap:"10px" }}>
     <span>{`$${Number(value || row?.recommendedMarketValuePerBarrel || 0).toFixed(2)}`}</span>
     {!CommonService.isAdmin() && (<Tooltip title="Edit" arrow>
      <IconButton size="small" onClick={handleEditButtonClick}>
        <EditIcon fontSize="small" />
      </IconButton>
      </Tooltip>) }
    </Box>
  );
};

const BarrelMarketValue: React.FC = () => {
  const [isLoading, setLoading] = useState(true);
  const [barrelMarketValue, setBarrelMarketValue] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const params = id === undefined ? CommonService.getCompanyId() : id;
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [show, setShow] = useState(false);
  const handleShow = () => setShow(true);


  const handleClose = () => {
    setShow(false);  
    setSelectedYear(new Date().getFullYear());
  };

  useEffect(() => {
    getBarrelMarketValue(page, pageSize);
  }, [])

  const onPaginationModelChange = (newPaginationModel) => {
    setPage(newPaginationModel.page);
    setPageSize(newPaginationModel.pageSize);

    getBarrelMarketValue(newPaginationModel.page, newPaginationModel.pageSize);
  };

  const getBarrelMarketValue = async (page:any, pageSize:any) => {
    try {
      setLoading(true);
      const response = await InventoryApiService.getBarrelMarketValue(params,page, pageSize);
      setBarrelMarketValue(response?.data?.content); // Save the response to state
      setTotalCount(response?.data?.totalElements);

    } catch (error) {
      console.error("Failed to fetch barrel market value", error);
    } finally {
      setLoading(false);
    }
  };

  // Define the localeText object based on the isLoading flag
  const localeText = {
    noRowsLabel: isLoading ? "Fetching records ..." : "No Record Found",
  };

  const updateBarrelMarketPrice = async (rowId:any, newValue:any, previousValue:any, recommendedMarketValuePerBarrel:any) => {

    //now show the warning message if the entered value is less than the recommended market value
    if (newValue < recommendedMarketValuePerBarrel) {
      CommonService.Toast.fire({
        title: "The value entered is less than the recommended amount.",
        icon: "error",
      });
      return; // Do not proceed with the API call
    }

    //now create an request object for updating the barrel market value
    const request = {
      barrelLotValueId : rowId,
      previousMarketValue: previousValue? previousValue : recommendedMarketValuePerBarrel,
      newMarketValue: newValue
    };

    //call the api and update the barrel market price
    try {
      setLoading(true)
      const response = await InventoryApiService.updateBarrelMarketValue(request);
      
      CommonService.Toast.fire({
        title: "Insurance value per container updated successfully",
        icon: "success",
      });

      //after successfully updating the barrel market value, call the getBarrelMarketValue function to get the updated data
      getBarrelMarketValue(page, pageSize);

    } catch(error) {
      console.error("Failed to update barrel market value", error);

      const errorMessage = error?.response?.data?.errorMessage ?? "";
      CommonService.Toast.fire({
        title: `${errorMessage}`,
        icon: "error",
      });

    } finally {
      setLoading(false)
    }
  };

  const showHistory = (id:any, lotId:any) => {
    //change the route and redirect to the history page
    const basePath = CommonService.isAdmin() ? "/master" : "/user";
    history.push(`${basePath}/market-value-history/${id}/${lotId}`);
  }

  const currentYear = new Date().getFullYear();

  const yearOptions = Array.from({ length: 4 }, (_, index) => {
    const year = currentYear - index;
    return { value: year, label: year };
  });

  const handleExportClick = async () => {
    try {
      const response = await InventoryApiService.downloadMarketValueReport(params, selectedYear);
      handleClose();
      CommonService.Toast.fire({
        title: "Your export is in progress. We'll notify you when it's ready!",
        icon: "success"
      });
      
    } catch (error) {
      console.log("Error occurred while exporting file")
    }
  }

  const ShowMarketValueChart = (lotId: any) => {
    const basePath = CommonService.isAdmin() ? "/master" : "/user";
    const path = CommonService.isAdmin()
      ? `${basePath}/barrel-market-value-chart-view/${id}/${lotId}`
      : `${basePath}/barrel-market-value-chart-view/${lotId}`;
      
    console.log(`${basePath}/barrel-market-value-chart-view/${id}/${lotId}`)
    history.push(path);
  };
  
  const columns = [
    {
      field: "barrelLotId",
      headerName: "Lot Id",
      sortable: false,
      flex: 1,
      minWidth: 80,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: () => (
        <Tooltip title="Unique ID for the barrel lot" arrow>
          <span>Lot Id</span>
        </Tooltip>
      ),
    },
    {
      field: "fillDate",
      headerName: "Fill Date",
      sortable: false,
      flex: 1,
      minWidth: 100,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: () => (
        <Tooltip title="The date the barrel was filled" arrow>
          <span>Fill Date</span>
        </Tooltip>
      ),
      renderCell: (params) => {
        return CommonService.formatDate(params?.value)
      },
    },
    {
      field: "age",
      headerName: "Age",
      sortable: false,
      flex: 1,
      minWidth: 120,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: () => (
        <Tooltip title="Age of the barrel contents" arrow>
          <span>Age</span>
        </Tooltip>
      ),
    },
    {
      field: "quantity",
      headerName: "Quantity",
      sortable: false,
      flex: 1,
      minWidth: 70,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: () => (
        <Tooltip title="Number of container in this lot" arrow>
          <span className="text-wrap">Quantity</span>
        </Tooltip>
      ),
    },
    {
      field: "currentMarketValuePerBarrel",
      headerName: "Current Insurance Value (Per Container)",
      sortable: false,
      flex: 1,
      minWidth: 160,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: () => (
        <Tooltip title="Current value of a single barrel for insurance" arrow>
          <span className="text-wrap">Current Insurance Value (Per Container)</span>
        </Tooltip>
      ),
      renderCell: (params) => (
        <div className="d-flex align-items-center h-100">
           <EditableCell
              value={params.value}
              row={params.row}
              onSave={(rowId, newValue, previousValue) => updateBarrelMarketPrice(rowId, newValue, previousValue, params?.row?.recommendedMarketValuePerBarrel)}
            />
        </div>
       
      ),
    },
    {
      field: "currentInsuranceValue",
      headerName: "Current Insurance Value",
      sortable: false,
      flex: 1,
      minWidth: 160,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: () => (
        <Tooltip title="Total value: Quantity × Current Per Container Value" arrow>
          <span className="text-wrap">Current Insurance Value</span>
        </Tooltip>
      ),
      valueGetter: (value: any, row: any) =>
      `$${((row?.quantity || 0) * (row?.currentMarketValuePerBarrel || row?.recommendedMarketValuePerBarrel || 0)).toFixed(2)}`
    },
    {
      field: "currentAnnualMarketCost",
      headerName: "Current Annual Insurance Cost (Billed Monthly)",
      sortable: false,
      flex: 1,
      minWidth: 150,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: () => (
        <Tooltip title="Annual cost of current insurance (charged monthly)" arrow>
          <span className="text-wrap">Current Annual Insurance Cost (Billed Monthly)</span>
        </Tooltip>
      ),
      renderCell: (params) => {
        const value = Number(params.value) || Number(params?.row?.recommendedMarketValueAnnualCost) || 0;
        return `$${isNaN(value) ? "-" : value.toFixed(2)}`;
      },
    },
    {
      field: "recommendedMarketValuePerBarrel",
      headerName: "Recommended Insurance Value (Per Container)",
      sortable: false,
      flex: 1,
     minWidth: 140,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: () => (
        <Tooltip title="Recommended value of a single barrel for insurance" arrow>
          <span className="text-wrap">Recommended Insurance Value (Per Container)</span>
        </Tooltip>
      ),
      valueGetter: (value: any, row: any) => 
       `$${(row?.recommendedMarketValuePerBarrel || 0).toFixed(2)}`
    },
    {
      field: "recommendedInsuranceValue",
      headerName: "Recommended Insurance Value",
      sortable: false,
      flex: 1,
     minWidth: 160,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: () => (
        <Tooltip title="Total value: Quantity × Recommended Per Container Value" arrow>
          <span className="text-wrap">Recommended Insurance Value</span>
        </Tooltip>
      ),
      valueGetter: (value: any, row: any) => 
       `$${((row?.quantity || 0) * (row?.recommendedMarketValuePerBarrel || 0)).toFixed(2)}`
    },
    {
      field: "recommendedMarketValueAnnualCost",
      headerName: "Recommended Annual Insurance Cost (Billed Monthly)",
      sortable: false,
      flex: 1,
      minWidth: 160,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: () => (
        <Tooltip title="Annual cost based on recommended values (charged monthly)" arrow>
          <span className="text-wrap">Recommended Annual Insurance Cost (Billed Monthly)</span>
        </Tooltip>
      ),
      renderCell: (params) => {
        const value = Number(params.value);
        return `$${isNaN(value) ? "-" : value.toFixed(2)}`;
      },
    },
    {
      field: "action",
      headerName: "Action",
      sortable: false,
      flex: 1,
      minWidth: 120,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: () => (
        <Tooltip title="Perform actions like edit or delete" arrow>
          <span>Action</span>
        </Tooltip>
      ),
      renderCell: (params) => {
        return (
          <>
          <Tooltip title="View History" arrow>
            <IconButton size="small" onClick={() => {showHistory(params?.row?.barrelLotValueId, params?.row?.barrelLotId)}}>
                <HistoryIcon/>
            </IconButton>
          </Tooltip>
          
          <Tooltip title="View Chart" arrow>
            <IconButton size="small" onClick={() => {ShowMarketValueChart(params?.row?.barrelLotId)}}>
              <BarChartIcon/>
            </IconButton>
          </Tooltip>
          </>
        );
      }
    },
  ];

  const handleBack = () => {
    history.goBack();
  }

  return (
    <>
    <div className="usersList">
      <div className="usersList__header--wrapper d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div className="d-flex align-items-center">
          {CommonService.isAdmin() && (
            <>
              <IconButton
                onClick={handleBack}
                aria-label="ArrowBackIcon"
                sx={{
                  backgroundColor: "#FFF",
                  "&:hover": { backgroundColor: "#FFF" },
                }}
              >
                <ArrowBackIcon sx={{ color: "#1C1B1F" }} />
              </IconButton>

              <div className="header--title me-2">
                <h1 className="title mb-0 ms-2">Insurance Values by Container Lot</h1>
              </div>
              <div className="divider mx-2"></div>
            </>
          )}
          <div className="d-flex align-items-center gap-2 flex-wrap left-wrapper">
            <div className="header--menu-items d-flex align-items-center flex-wrap gap-2">
              {/*Removing the year filter from table will ask year at the time of exporting data */}
              {/* <div>
                <select
                  id="year"
                  className="form-select"
                  value={selectedYear}
                  style={{ width: 'auto' }}
                  onChange={(e) => {
                    const selectedYear = Number(e.target.value);
                    setSelectedYear(selectedYear);
                  }}
                >
                  {yearOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div> */}
              <button title="Export to Excel"
                className="primary-btn d-inline-flex align-items-center"
                style={{ paddingTop:"4px",paddingLeft:11}}
                onClick={() => setShow(true)}
              >
                <FileDownloadOutlinedIcon sx={{marginTop:"5.5px"}}/>
                Export
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="user-table-container">
        <div className="userTable">
          <DataGrid
            rows={barrelMarketValue}
            className="data-grid support-req--listing"
            columns={columns}
            getRowId={(row) => row?.barrelLotValueId}
            localeText={localeText}
            initialState={{
              pagination: {
                paginationModel: { page, pageSize },
              },
            }}
            pagination
            paginationModel={{ page, pageSize }}
            rowCount={totalCount}
            paginationMode="server"
            sortingMode="client"
            pageSizeOptions={[10, 20, 50, 100]}
            onPaginationModelChange={onPaginationModelChange}
            loading={isLoading}
            slots={{
              loadingOverlay: DataGridLoader,
            }}
            disableColumnMenu
            disableColumnResize
            disableRowSelectionOnClick
          />
        </div>
      </div>
    </div>

     <Modal
      show={show}
      onHide={handleClose}
      className="modal__wd modal__wd--submitSellReq"
    >
      <Modal.Header className="flex-column align-items-start">
        <Modal.Title>Select Year</Modal.Title>
        <p className="modal-text">Select the year for which you want to export data</p>
      </Modal.Header>

      <Modal.Body>
        <div className="form__inner">
          <label
            style={{
              color: "#333333",
              fontWeight: "700",
              marginBottom: "6px",
            }}
          >
            Year <span className="star-required text-danger">*</span>
          </label>
          <Form.Select
            as="select"
            value={selectedYear}
            onChange={(e) => {
                const selectedYear = Number(e.target.value);
                setSelectedYear(selectedYear);
            }}
            className='form-control no-spinner'
          >
            {yearOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </div>
      </Modal.Body>

      <Modal.Footer className="justify-content-center">
        <div className="btn-group row d-flex justify-content-between w-100">
          <div className="col-6 ps-0">
            <button
              className="secondary-btn w-100"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>

          <div className="col-6 pe-0">
            <button
              className="primary-btn w-100"
              onClick={() => handleExportClick()}
              disabled={isLoading}
            >
              {isLoading && <Spinner />} Export
            </button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>

    </>
  );
};

export default BarrelMarketValue;
